import React from "react";
import { DataGrid } from "@mui/x-data-grid";

/** MUI X DataGrid (MIT) rejects `pageSize` greater than this. */
const MIT_DATAGRID_MAX_PAGE_SIZE = 100;
import {
  dataGridAmbientBaseSx,
  gridRowHoverSx,
  gridRowStripeSx,
  SLATE_900,
  TH_BG_DARK,
  TABLE_BORDER_AMBIENT,
  TABLE_TEXT_PRIMARY_DARK,
} from "../../utils/colors";

const CommonDataGrid = ({
  firstColumnField,
  frozenHeaderBg,
  frozenCellBg,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  initialState,
  /**
   * When false, footer pagination (page size + page controls) is hidden and a large
   * client page size is used so all rows show. (Community DataGrid always has
   * pagination enabled internally — it cannot be turned off.)
   */
  pagination = true,
  /** Used when pagination is false so all typical rows fit on one “page”. */
  unpagedRowCap = 500,
  disableRowSelectionOnClick = true,
  enableBaseStyles = true,
  enableSearch = true,
  searchPlaceholder = "Search...",
  showToolbar,
  slotProps,
  sx,
  rows,
  ...props
}) => {
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  /** Unpaged mode uses a synthetic large page; that size must stay within MIT limits. */
  const unpagedExceedsMitLimit = !pagination && rowCount > MIT_DATAGRID_MAX_PAGE_SIZE;
  const effectivePagination = pagination || unpagedExceedsMitLimit;
  const resolvedPageSizeOptions =
    effectivePagination && unpagedExceedsMitLimit && !pageSizeOptions.includes(MIT_DATAGRID_MAX_PAGE_SIZE)
      ? [...pageSizeOptions, MIT_DATAGRID_MAX_PAGE_SIZE].sort((a, b) => a - b)
      : pageSizeOptions;

  const freezeSx = firstColumnField
    ? {
        "& .MuiDataGrid-cell": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiDataGrid-row:nth-of-type(even)": {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.015)",
        },
        "& .MuiDataGrid-columnHeaders": {
          fontWeight: 700,
          fontSize: "0.8rem",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          overflow: "visible",
          alignItems: "flex-start",
          pt: 1,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "bold",
          color: (theme) =>
            theme.palette.mode === "dark" ? TABLE_TEXT_PRIMARY_DARK : SLATE_900,
        },
        "& .MuiDataGrid-row": {
          gridRowHoverSx,
          ...gridRowStripeSx,
          transition: "background 0.15s",
        },
        "& .MuiDataGrid-columnHeadersInner": {
          overflow: "visible",
        },
        [`& .MuiDataGrid-columnHeadersInner .MuiDataGrid-columnHeader[data-field="${firstColumnField}"]`]:
          {
            position: "sticky",
            left: 0,
            zIndex: 6,
            bgcolor: (theme) => {
              if (frozenHeaderBg !== undefined) return frozenHeaderBg;
              return theme.palette.mode === "dark" ? TH_BG_DARK : theme.palette.background.paper;
            },
            borderRight: (theme) =>
              theme.palette.mode === "dark"
                ? `1px solid ${TABLE_BORDER_AMBIENT}`
                : "1px solid",
            borderColor: "divider",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "2px 0 8px rgba(0,0,0,0.2)"
                : "2px 0 6px rgba(0,0,0,0.08)",
          },
        [`& .MuiDataGrid-cell[data-field="${firstColumnField}"]`]: {
          position: "sticky",
          left: 0,
          zIndex: 5,
          bgcolor: (theme) => {
            if (frozenCellBg !== undefined) return frozenCellBg;
            return theme.palette.mode === "dark" ? "transparent" : theme.palette.background.paper;
          },
          borderRight: (theme) =>
            theme.palette.mode === "dark"
              ? `1px solid ${TABLE_BORDER_AMBIENT}`
              : "1px solid",
          borderColor: "divider",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "2px 0 6px rgba(0,0,0,0.15)"
              : "2px 0 6px rgba(0,0,0,0.05)",
          alignItems: "flex-start",
          py: 1.5,
        },
      }
    : {};

  const baseSx = enableBaseStyles ? dataGridAmbientBaseSx : {};

  const mergedInitialState = (() => {
    if (!effectivePagination) {
      const onePageSize = Math.min(
        Math.max(rowCount, 1),
        MIT_DATAGRID_MAX_PAGE_SIZE,
        unpagedRowCap
      );
      const big = { paginationModel: { page: 0, pageSize: onePageSize } };
      if (initialState) {
        return initialState.pagination
          ? initialState
          : { ...initialState, pagination: big };
      }
      return { pagination: big };
    }
    if (initialState) {
      return initialState.pagination
        ? initialState
        : {
            ...initialState,
            pagination: { paginationModel: { pageSize: defaultPageSize } },
          };
    }
    return { pagination: { paginationModel: { pageSize: defaultPageSize } } };
  })();

  const toolbarSlotProps = {
    ...slotProps?.toolbar,
    showQuickFilter: true,
    quickFilterProps: {
      debounceMs: 300,
      placeholder: searchPlaceholder,
      ...slotProps?.toolbar?.quickFilterProps,
    },
  };

  const mergedSlotProps = enableSearch
    ? {
        ...slotProps,
        toolbar: toolbarSlotProps,
      }
    : slotProps;

  return (
    <DataGrid
      {...props}
      rows={rows ?? []}
      hideFooterPagination={!effectivePagination}
      pageSizeOptions={effectivePagination ? resolvedPageSizeOptions : undefined}
      initialState={mergedInitialState}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      showToolbar={showToolbar ?? enableSearch}
      slotProps={mergedSlotProps}
      sx={[baseSx, freezeSx, sx]}
      // density="comfortable"
      rowHeight={64}
      columnHeaderHeight={72}
    />
  );
};

export default CommonDataGrid;
